<template>
  <div class="feedback-modal" @click="$emit('close')">
    <div class="feedback-modal-content" @click.stop>
      <div class="feedback-header">
        <h3 class="feedback-title">意见反馈</h3>
        <button class="modal-close-btn" @click="$emit('close')">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>
      
      <div class="feedback-body">
        <p class="feedback-description">
          请详细描述您的意见或建议，我们会认真阅读并改进
        </p>
        
        <!-- 反馈类型 -->
        <div class="form-group">
          <label class="form-label">反馈类型</label>
          <div class="feedback-types">
            <button
              v-for="type in feedbackTypes"
              :key="type.value"
              class="type-btn"
              :class="{ active: form.type === type.value }"
              @click="form.type = type.value"
            >
              <i :class="type.icon"></i>
              {{ type.label }}
            </button>
          </div>
        </div>

        <!-- 反馈内容 -->
        <div class="form-group">
          <label class="form-label">反馈内容 <span class="required">*</span></label>
          <textarea
            v-model="form.content"
            class="feedback-textarea"
            :class="{ 'input-error': contentError }"
            placeholder="请详细描述您遇到的问题或建议..."
            rows="5"
            maxlength="1000"
            @input="validateContent"
          ></textarea>
          <div class="textarea-footer">
            <span v-if="contentError" class="field-error">{{ contentError }}</span>
            <span class="char-count">{{ form.content.length }}/1000</span>
          </div>
        </div>

        <!-- 联系方式（可选） -->
        <div class="form-group">
          <label class="form-label">联系方式（可选）</label>
          <input
            v-model="form.contact"
            type="text"
            class="feedback-input"
            placeholder="邮箱或手机号，方便我们联系您"
            maxlength="100"
          />
        </div>

        <!-- 提交按钮 -->
        <button
          class="submit-btn"
          :class="{ 'is-loading': isSubmitting }"
          :disabled="isSubmitting"
          @click="handleSubmit"
        >
          <i v-if="isSubmitting" class="fas fa-spinner fa-spin"></i>
          {{ isSubmitting ? '提交中...' : '提交反馈' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue'
import api from '@/services/api'
import { debugError } from '@/utils/env'

// 定义emit
const emit = defineEmits<{
  close: []
}>()

// 反馈类型
const feedbackTypes = [
  { value: 'bug', label: '功能异常', icon: 'fas fa-bug' },
  { value: 'suggestion', label: '功能建议', icon: 'fas fa-lightbulb' },
  { value: 'content', label: '内容问题', icon: 'fas fa-file-alt' },
  { value: 'other', label: '其他', icon: 'fas fa-comment' }
]

// 表单数据
const form = reactive({
  type: 'suggestion',
  content: '',
  contact: ''
})

const isSubmitting = ref(false)
const contentError = ref('')

// 验证内容
const validateContent = () => {
  if (form.content.trim().length === 0) {
    contentError.value = '请输入反馈内容'
    return false
  }
  if (form.content.trim().length < 10) {
    contentError.value = '反馈内容至少10个字符'
    return false
  }
  contentError.value = ''
  return true
}

// 提交反馈
const handleSubmit = async () => {
  if (!validateContent()) {
    return
  }

  isSubmitting.value = true

  try {
    const result: any = await api.post('/feedback', {
      type: form.type,
      content: form.content.trim(),
      contact: form.contact.trim() || null
    })

    if (result.errorcode === '0') {
      // 提交成功
      if ((window as any).globalModal?.showMessage) {
        (window as any).globalModal.showMessage('感谢反馈', '您的意见已提交，我们会认真考虑并改进', 'info')
      }
      emit('close')
    } else {
      throw new Error(result.errormsg || '提交失败')
    }
  } catch (error: any) {
    debugError('提交反馈失败:', error)
    let errorMsg = '网络错误，请稍后重试'
    if (error.response?.data?.errormsg) {
      errorMsg = error.response.data.errormsg
    } else if (error.message) {
      errorMsg = error.message
    }
    if ((window as any).globalModal?.showMessage) {
      (window as any).globalModal.showMessage('提交失败', errorMsg, 'error')
    }
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
/* 反馈弹窗样式 */
.feedback-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal);
  padding: 20px;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.feedback-modal-content {
  background: white;
  border-radius: 20px;
  width: min(500px, 90vw);
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  z-index: var(--z-modal);
}

.feedback-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 0 24px;
  margin-bottom: 20px;
}

.feedback-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--dark-text);
  font-family: 'Georgia', 'Times New Roman', serif;
}

.feedback-body {
  padding: 0 24px 24px 24px;
}

.feedback-description {
  margin: 0 0 24px 0;
  font-size: 14px;
  color: var(--light-text);
  line-height: 1.6;
}

/* 表单样式 */
.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--dark-text);
  margin-bottom: 8px;
}

.required {
  color: #f56c6c;
}

/* 反馈类型选择 */
.feedback-types {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.type-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  border: 2px solid rgba(139, 115, 85, 0.2);
  border-radius: 12px;
  background: white;
  color: var(--dark-text);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.type-btn:hover {
  border-color: var(--secondary-color);
  background: rgba(139, 115, 85, 0.05);
}

.type-btn.active {
  border-color: var(--secondary-color);
  background: rgba(139, 115, 85, 0.1);
  color: var(--primary-color);
}

.type-btn i {
  font-size: 16px;
}

/* 文本域 */
.feedback-textarea {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid rgba(139, 115, 85, 0.2);
  border-radius: 12px;
  font-size: 14px;
  font-family: inherit;
  line-height: 1.6;
  resize: vertical;
  min-height: 120px;
  transition: all 0.3s ease;
  box-sizing: border-box;
  color: var(--dark-text);
}

.feedback-textarea:focus {
  outline: none;
  border-color: var(--secondary-color);
  box-shadow: 0 0 0 3px rgba(139, 115, 85, 0.1);
}

.feedback-textarea::placeholder {
  color: var(--light-text);
}

/* 输入框 */
.feedback-input {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid rgba(139, 115, 85, 0.2);
  border-radius: 12px;
  font-size: 14px;
  font-family: inherit;
  transition: all 0.3s ease;
  box-sizing: border-box;
  color: var(--dark-text);
}

.feedback-input:focus {
  outline: none;
  border-color: var(--secondary-color);
  box-shadow: 0 0 0 3px rgba(139, 115, 85, 0.1);
}

.feedback-input::placeholder {
  color: var(--light-text);
}

/* 文本域底部 */
.textarea-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 6px;
}

.char-count {
  font-size: 12px;
  color: var(--light-text);
  margin-left: auto;
}

/* 错误样式 */
.input-error {
  border-color: #f56c6c !important;
  box-shadow: 0 0 0 3px rgba(245, 108, 108, 0.1) !important;
}

.field-error {
  color: #f56c6c;
  font-size: 12px;
}

/* 提交按钮 */
.submit-btn {
  width: 100%;
  padding: 14px 24px;
  background: var(--gradient-primary);
  color: white;
  border: none;
  border-radius: 25px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: 'Georgia', 'Times New Roman', serif;
  margin-top: 8px;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(139, 115, 85, 0.3);
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* 关闭按钮 */
.modal-close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.1);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: var(--z-content);
}

.modal-close-btn:hover {
  background: rgba(0, 0, 0, 0.2);
  transform: scale(1.1);
}

.modal-close-btn svg {
  width: 20px;
  height: 20px;
  color: #666;
}

/* 响应式设计 */
@media (max-width: 480px) {
  .feedback-modal-content {
    width: min(400px, 95vw);
    margin: 10px;
    border-radius: 16px;
  }
  
  .feedback-header {
    padding: 20px 20px 0 20px;
  }
  
  .feedback-body {
    padding: 0 20px 20px 20px;
  }
  
  .feedback-types {
    grid-template-columns: 1fr;
  }
  
  .feedback-title {
    font-size: 18px;
  }
}
</style>
